import { createHttpClient } from '@adapters/HttpClient';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { BankAccount, BankAccountsResponse } from '@modules/Wallets/BankAccounts/models/BankAccount';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { TextArea } from '@shared/components/ui/textarea';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { getErrorMessage } from '@shared/utils/error.utils';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BankAccountsApiAdapter } from '../../../BankAccounts/adapters/BankAccountsApiAdapter';
import { useWallet } from '../../../Wallet/hooks/useWallet';
import { useWithdrawalsMutations } from '../../hooks/useWithdrawalsMutations';

type WithdrawalsTFunction = TFunction<'withdrawalsModule'>;

function getCreateWithdrawalErrorDescription(error: unknown, t: WithdrawalsTFunction): string {
  const axiosPayload = error as { response?: { data?: { error?: { error_code?: string } } } };
  const code = axiosPayload?.response?.data?.error?.error_code;
  if (code === 'PENDING_WITHDRAWAL_ALREADY_EXISTS') {
    return t('errors.codes.PENDING_WITHDRAWAL_ALREADY_EXISTS');
  }
  return getErrorMessage(error);
}

export interface CreateWithdrawalModalRef {
  open: (walletId: number) => void;
  close: () => void;
}

interface CreateWithdrawalModalProps {
  onSuccess?: () => void;
}

export const CreateWithdrawalModal = forwardRef<CreateWithdrawalModalRef, CreateWithdrawalModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('withdrawalsModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const { createWithdrawal } = useWithdrawalsMutations();
    const currency = useCurrency();

    const [walletId, setWalletId] = useState<number | null>(null);
    const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
    const bankAccountsAdapter = useMemo(() => new BankAccountsApiAdapter(httpClient), [httpClient]);

    // Obtener el saldo de la billetera
    const { data: walletDetail } = useWallet(walletId ?? undefined);

    const { data: bankAccountsData, isLoading: bankAccountsLoading } = useQuery<BankAccountsResponse>({
      queryKey: ['withdrawals', 'bank-accounts'],
      queryFn: () =>
        bankAccountsAdapter.getBankAccounts({
          advanced_filters: {
            filters: [{ column: 'status', operator: 'in', value: ['VERIFIED'] }],
            logic: 'AND',
          },
        }),
      staleTime: 60 * 1000,
    });

    const bankAccounts: BankAccount[] = bankAccountsData?.data ?? [];

    const [bankAccountId, setBankAccountId] = useState<number | null>(null);
    const [requestedAmount, setRequestedAmount] = useState('');
    const [internalNote, setInternalNote] = useState('');

    useImperativeHandle(ref, () => ({
      open: (wallet) => {
        setWalletId(wallet);
        modalRef.current?.openModal();
      },
      close: () => modalRef.current?.closeModal(),
    }));

    const resetForm = () => {
      setBankAccountId(null);
      setRequestedAmount('');
      setInternalNote('');
    };

    const handleSubmit = async () => {
      if (!walletId) return;
      try {
        await createWithdrawal.mutateAsync({
          walletId,
          payload: {
            bank_account_id: Number(bankAccountId),
            requested_amount: Number(requestedAmount),
            internal_note: internalNote.trim(),
          },
        });

        toast({
          title: t('modals.create.success.title'),
          description: t('modals.create.success.description'),
        });
        resetForm();
        modalRef.current?.closeModal();
        onSuccess?.();
      } catch (error) {
        toast({
          title: t('errors.createFailed'),
          description: getCreateWithdrawalErrorDescription(error, t),
          variant: 'destructive',
        });
      }
    };

    const isLoading = createWithdrawal.isPending;
    const hasVerifiedAccounts = bankAccounts.length > 0;
    const walletBalance = walletDetail?.balance_total ?? 0;
    const requestedAmountNum = Number(requestedAmount);
    const exceedsBalance = requestedAmountNum > walletBalance;
    const isFormValid =
      walletId &&
      bankAccountId !== null &&
      requestedAmount.trim() &&
      requestedAmountNum > 0 &&
      !exceedsBalance &&
      hasVerifiedAccounts;

    return (
      <ModalCenter ref={modalRef} title={t('modals.create.title')} type="default" width="lg" className="max-h-[90vh]">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('modals.create.description')}</p>

          {!bankAccountsLoading && !hasVerifiedAccounts && (
            <Alert variant="destructive" className="border-warning/80 bg-warning/10">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-warning mt-0.5" />
                <div className="flex-1">
                  <AlertTitle className="text-warning text-base !mb-1">
                    {t('modals.create.noBankAccounts.title')}
                  </AlertTitle>
                  <AlertDescription className="text-sm">
                    {t('modals.create.noBankAccounts.description')}
                  </AlertDescription>
                </div>
              </div>
            </Alert>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.bank_account_id')} *</label>
            <Select
              value={bankAccountId !== null ? String(bankAccountId) : ''}
              onValueChange={(value) => setBankAccountId(Number(value))}
              disabled={isLoading || bankAccountsLoading || !hasVerifiedAccounts}
            >
              <SelectTrigger className="h-auto min-h-10 w-full py-2 whitespace-normal [&>span]:line-clamp-none">
                {/*
                  No pasar JSX custom como hijos de SelectValue: Radix controla ese nodo y provoca
                  removeChild al cambiar selección. Usar textValue en SelectItem para el texto del trigger.
                */}
                <SelectValue
                  placeholder={
                    bankAccountsLoading
                      ? t('loading.withdrawals')
                      : hasVerifiedAccounts
                        ? t('modals.create.fields.bank_account_id')
                        : t('modals.create.noBankAccounts.selectPlaceholder')
                  }
                />
              </SelectTrigger>
              <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                {bankAccounts.map((acc) => (
                  <SelectItem
                    key={acc.id}
                    value={String(acc.id)}
                    textValue={[acc.holder_name, acc.iban, acc.bank_name].filter(Boolean).join(' · ')}
                    className="py-3"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium">{acc.holder_name}</span>
                      <span className="text-xs text-muted-foreground font-mono">{acc.iban}</span>
                      <span className="text-xs text-muted-foreground [&:empty]:hidden">{acc.bank_name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                {t('modals.create.fields.requested_amount')} *
              </label>
              {walletId && walletDetail && (
                <span className="text-xs text-muted-foreground">
                  {t('modals.create.fields.available_balance')}: {formatCurrencyAmount(walletBalance, currency)}
                </span>
              )}
            </div>
            <Input
              type="number"
              value={requestedAmount}
              onChange={(e) => setRequestedAmount(e.target.value)}
              disabled={isLoading || !hasVerifiedAccounts}
              placeholder="100"
              min="0"
              max={walletBalance}
              step="0.01"
            />
            {exceedsBalance && (
              <p className="text-xs text-destructive">
                {t('modals.create.errors.exceedsBalance', { balance: walletBalance.toFixed(2), currency })}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.internal_note')} *</label>
            <TextArea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              disabled={isLoading || !hasVerifiedAccounts}
              rows={3}
              placeholder={t('modals.create.fields.internal_note_placeholder')}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isLoading}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid || isLoading}>
              {isLoading ? t('buttons.creating') : t('buttons.confirm')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

CreateWithdrawalModal.displayName = 'CreateWithdrawalModal';
